import { addMonths, startOfMonth, addMinutes, addDays } from "date-fns";
import { AccountHolder, Subscription } from "@rentablo/api/apiv1";
import { AccountTypeEnum } from "@features/cashbackWizard2.0/types/cashbackWizardContext";
import { isReducedFlow } from "@features/cashbackWizard2.0/utils/guards";
import { CashbackWizardContext, Values } from "../types";

const isBank = (bic: Bic) => (attrs: Partial<Values>) => attrs.bic === bic;

export const isDAB = isBank("DABBDEMMXXX");
export const isFNZ = isBank("COBADEMXXXX");
export const isComdirect = isBank("COBADEHDXXX");
export const isFondsdepot = isBank("FODBDE77XXX");
export const isAugsburgerAktienbank = isBank("AUGBDE77XXX");
export const isDWS = isBank("DEUTDEFFXXX");
export const isMorgenFund = isBank("DEUTLU2DXXX");
export const isFFB = isBank("FFBKDEFFTHK");
export const isMetzler = isBank("METZDEFFXXX");

export const isNewDepot = ({ wizardType }: CashbackWizardContext) =>
  wizardType === "newUser";

export const isExistingUser = ({ wizardType }: CashbackWizardContext) =>
  wizardType === "existingUser";

export const isCompanyDepot = ({ accountType }: CashbackWizardContext) =>
  accountType === AccountTypeEnum.COMPANY;

export const isPrivateDepot = ({ accountType }: CashbackWizardContext) =>
  accountType === AccountTypeEnum.PRIVATE;

export const isMifidAndTaxationRequired = (
  context: CashbackWizardContext
): boolean =>
  !isReducedFlow(context) &&
  isPrivateDepot(context) &&
  isNewDepot(context) &&
  (isDAB(context.values) ||
    isFNZ(context.values) ||
    isComdirect(context.values) ||
    isFondsdepot(context.values) ||
    isFFB(context.values));

export const isSingleAccount = ({ accountHolderType }: CashbackWizardContext) =>
  accountHolderType === "single";

export const isJoinedAccount = ({ accountHolderType }: CashbackWizardContext) =>
  accountHolderType === "joined";

export const isMinorAccount = ({ accountHolderType }: CashbackWizardContext) =>
  accountHolderType === "minor";

export const sameDateInUTC = (date: Date) =>
  addMinutes(
    date,
    -date.getTimezoneOffset() // it fixes timezone for saving to backend as date-fns calculates always in local timezone
  );

export const startDate = (currentDate = new Date()) =>
  startOfMonth(addMonths(addDays(currentDate, 25), 1));

export const startDateUTC = (currentDate = new Date()) =>
  sameDateInUTC(startDate(currentDate));

export const requiresPostident = (subscription?: Subscription) => {
  return (
    subscription?.customData?.isNewDepot &&
    ["COBADEHDXXX", "COBADEMXXXX", "DABBDEMMXXX"].includes(
      subscription?.bank?.defaultBic || ""
    )
  );
};

export const hasSecondLegalGuardian = (context: CashbackWizardContext) =>
  context.values.hasSecondLegalGuardian;

export const requiresIdentificationByRentablo = (
  subscription?: Subscription
) => {
  return (
    subscription?.customData?.isNewDepot &&
    ["AUGBDE77XXX", "FFBKDEFFTHK", "FODBDE77XXX"].includes(
      subscription?.bank?.defaultBic || ""
    )
  );
};

export const requiresBirthCertificate = (
  subscription?: Subscription,
  accountHolder?: AccountHolder
) => {
  return subscription?.customData?.isNewDepot &&
    accountHolder?.naturalPersons &&
    accountHolder?.naturalPersons?.length > 0
    ? accountHolder?.naturalPersons[0].isMinor
    : false;
};

export const isStandardModel = (context: CashbackWizardContext) =>
  context.values.comdirectConditionModel === "conditionModel5";

export const isFlatFeeModel = (context: CashbackWizardContext) =>
  context.values.comdirectConditionModel === "conditionModel3";

export const getAccountIdentifier = (values: Values) => {
  switch (values.bic) {
    case "DABBDEMMXXX":
      return values.dabAccountNumber;
    case "FODBDE77XXX":
      return values.fondsdepotDepotNumber;
    case "COBADEHDXXX":
      return values.comdirectCustomerNumber;
    case "METZDEFFXXX":
      return values.metzlerDepotNumber;
    case "FFBKDEFFTHK":
      return values.ffbDepotNumber;
    case "AUGBDE77XXX":
      return values.augsburgerAktienbankDepotNumber;
    case "DEUTDEFFXXX":
    case "DEUTLU2DXXX":
      return values.dwsDepotNumber;
    case "COBADEMXXXX":
      return values.ebaseDepotNumber;
    default:
      return "";
  }
};
